<script setup>
import { ref, computed, onMounted } from 'vue';
import { condaApi } from '../condaApi';

// The agent playbook — patterns learned from how HUMAN agents handle the
// conversations the bot couldn't. Separate from the knowledge base on purpose:
// these feed ONLY the "Suggest reply" drafts agents see, never the bot.

const items = ref([]);
const loading = ref(false);
const scanMsg = ref('');
const err = ref('');
const filter = ref('all'); // all | kb

async function load() {
  loading.value = true;
  try { items.value = await condaApi.playbook(); } catch (e) { err.value = e.message; }
  loading.value = false;
}
onMounted(load);

async function del(item) {
  if (!confirm('Forget this pattern? It will no longer shape suggested replies.')) return;
  try { await condaApi.playbookDelete(item.id); load(); } catch (e) { err.value = e.message; }
}

// ── Promote a KB candidate ────────────────────────────────────────────────
// Draft (LLM shapes the fact into an entry) → operator edits → Add creates it.
const kbDraft = ref(null);      // { itemId, slug, category, description, response }
const drafting = ref(0);        // item id being drafted
const promoting = ref(false);

async function startPromote(item) {
  drafting.value = item.id;
  err.value = '';
  try {
    const d = await condaApi.playbookDraftKb(item.id);
    kbDraft.value = { itemId: item.id, ...d };
  } catch (e) {
    err.value = e.message;
  }
  drafting.value = 0;
}

async function confirmPromote() {
  const d = kbDraft.value;
  if (!d?.slug || !d?.response) { err.value = 'Name/ID and answer are required.'; return; }
  promoting.value = true;
  err.value = '';
  try {
    await condaApi.playbookPromote(d.itemId, {
      slug: d.slug, category: d.category, description: d.description, response: d.response,
    });
    kbDraft.value = null;
    scanMsg.value = `✓ Added “${d.slug}” to the knowledge base — the bot can use it right away.`;
    load();
  } catch (e) {
    err.value = e.message;
  }
  promoting.value = false;
}

const shown = computed(() =>
  filter.value === 'kb' ? items.value.filter(i => i.kb_worthy) : items.value
);
const kbCount = computed(() => items.value.filter(i => i.kb_worthy).length);
</script>

<template>
  <div class="conda-page">
    <div class="head">
      <div class="row">
        <h1>Playbook</h1>
        <span class="muted" style="font-size:12px">
          How your agents handle what the bot can't — learned automatically when a handed-over chat is resolved. Used only for suggested replies.
        </span>
      </div>
      <button class="btn" :disabled="loading" @click="load">{{ loading ? '…' : 'Refresh' }}</button>
    </div>

    <div v-if="err" class="banner err">{{ err }}</div>
    <div v-if="scanMsg" class="banner" style="background:#14241a;border-color:#1f5130;color:#9be3b0">{{ scanMsg }}</div>

    <div class="row filters">
      <button class="btn" :class="{ primary: filter === 'all' }" @click="filter = 'all'">
        All patterns ({{ items.length }})
      </button>
      <button class="btn" :class="{ primary: filter === 'kb' }" @click="filter = 'kb'">
        📌 KB candidates ({{ kbCount }})
      </button>
    </div>

    <div class="list">
      <div v-if="loading" class="empty">Loading…</div>
      <div v-else-if="!shown.length" class="empty">
        {{ filter === 'kb'
          ? 'No KB candidates yet — patterns that contain a generalizable fact will show here.'
          : 'Nothing learned yet. Patterns appear here automatically once agents handle and resolve handed-over conversations.' }}
      </div>
      <div v-for="it in shown" :key="it.id" class="item" style="cursor:default">
        <div class="row between">
          <strong>When the customer: {{ it.customer_pattern }}</strong>
          <span class="row" style="gap:6px;flex:0 0 auto">
            <span class="pill">seen {{ it.seen_count }}×</span>
            <span v-if="it.kb_worthy" class="tag green">📌 KB candidate</span>
          </span>
        </div>
        <div style="margin-top:8px;white-space:pre-wrap">{{ it.agent_reply }}</div>
        <div v-if="it.kb_worthy && it.kb_note" class="kb-note">
          <strong>Worth adding to the knowledge base:</strong> {{ it.kb_note }}

          <!-- Draft → review → add. Nothing goes in without the operator's eyes. -->
          <div v-if="it.promoted_at" style="margin-top:6px"><span class="tag green">✓ added to the KB</span></div>
          <div v-else-if="kbDraft && kbDraft.itemId === it.id" style="margin-top:10px">
            <div v-if="kbDraft.similar && kbDraft.similar.length" class="banner" style="margin-bottom:8px">
              ⚠ The knowledge base may already cover this —
              <strong>{{ kbDraft.similar.map(s => s.slug).join(', ') }}</strong> looks similar.
              Consider editing that entry on the Knowledge page instead of adding a new one.
            </div>
            <label>Name / ID</label>
            <input v-model="kbDraft.slug" class="in" />
            <label>Category</label>
            <input v-model="kbDraft.category" class="in" />
            <label>What this entry answers</label>
            <input v-model="kbDraft.description" class="in" />
            <label>Answer (what the bot will say)</label>
            <textarea v-model="kbDraft.response" class="in" rows="4" />
            <div class="row" style="margin-top:10px">
              <button class="btn primary" :disabled="promoting" @click="confirmPromote">
                {{ promoting ? 'Adding…' : 'Add to knowledge base' }}
              </button>
              <button class="btn" :disabled="promoting" @click="kbDraft = null">Cancel</button>
            </div>
          </div>
          <div v-else style="margin-top:8px">
            <button class="btn" :disabled="drafting === it.id" @click="startPromote(it)">
              {{ drafting === it.id ? 'Drafting…' : '📌 Draft KB entry' }}
            </button>
            <span class="muted sm" style="margin-left:8px">You review and edit it before anything is added.</span>
          </div>
        </div>
        <div class="row between" style="margin-top:10px">
          <span class="muted" style="font-size:11px">
            learned from {{ (it.learned_from || []).join(', ') }}
            <span v-if="it.example_message"> · e.g. “{{ it.example_message.slice(0, 90) }}”</span>
          </span>
          <button class="btn danger" @click="del(it)">Forget</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped src="./conda.css"></style>

<style scoped>
.kb-note {
  margin-top: 10px;
  padding: 8px 12px;
  border-left: 3px solid #22c55e;
  background: rgba(34, 197, 94, 0.07);
  border-radius: 0 8px 8px 0;
  font-size: 13px;
}
</style>
